/**
 * Datumslogik auf ISO-Strings ("YYYY-MM-DD") statt auf `Date`.
 *
 * Beide Quell-APIs liefern reine Kalendertage ohne Zeitzone. Sobald man die
 * durch `new Date()` schickt, wandert je nach Serverzeitzone ein Buchungsdatum
 * ueber die Monatsgrenze — und damit landet eine Rechnung im falschen
 * Umsatzsteuer-Voranmeldungszeitraum. Deshalb rechnen wir hier durchgehend
 * mit UTC-Mitternacht und geben immer wieder Strings zurueck.
 */
const DAY_MS = 86_400_000;
export function toIsoDate(d) {
    return d.toISOString().slice(0, 10);
}
/** Parst "YYYY-MM-DD" zu UTC-Mitternacht. Wirft bei ungueltigem Datum. */
export function parseIsoDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m)
        throw new Error(`Ungueltiges Datum: ${iso}`);
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    if (Number.isNaN(d.getTime()))
        throw new Error(`Ungueltiges Datum: ${iso}`);
    return d;
}
export function addDays(iso, days) {
    return toIsoDate(new Date(parseIsoDate(iso).getTime() + days * DAY_MS));
}
/** Ganze Tage zwischen zwei Daten (b - a). Negativ, wenn b vor a liegt. */
export function daysBetween(a, b) {
    return Math.round((parseIsoDate(b).getTime() - parseIsoDate(a).getTime()) / DAY_MS);
}
export function monthKey(iso) {
    return iso.slice(0, 7);
}
export function startOfMonth(key) {
    return `${key.slice(0, 7)}-01`;
}
export function endOfMonth(key) {
    const year = Number(key.slice(0, 4));
    const month = Number(key.slice(5, 7));
    return toIsoDate(new Date(Date.UTC(year, month, 0)));
}
/** Verschiebt einen Monatsschluessel, z. B. addMonths("2026-11", 2) → "2027-01". */
export function addMonths(key, months) {
    const year = Number(key.slice(0, 4));
    const month = Number(key.slice(5, 7)) - 1 + months;
    const d = new Date(Date.UTC(year, month, 1));
    return toIsoDate(d).slice(0, 7);
}
/** Aufsteigende Liste von Monatsschluesseln, Grenzen inklusive. */
export function monthRange(from, to) {
    const out = [];
    let cur = from.slice(0, 7);
    // Harte Obergrenze, damit ein vertauschtes Argumentpaar keine Endlosschleife wird.
    for (let i = 0; i < 600 && cur <= to; i++) {
        out.push(cur);
        cur = addMonths(cur, 1);
    }
    return out;
}
export function isWeekend(iso) {
    const day = parseIsoDate(iso).getUTCDay();
    return day === 0 || day === 6;
}
/** Ostersonntag nach dem anonymen gregorianischen Algorithmus (Meeus/Jones/Butcher). */
export function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return toIsoDate(new Date(Date.UTC(year, month - 1, day)));
}
const holidayCache = new Map();
/** Gesetzliche Feiertage eines Jahres: bundeseinheitlich plus die des Landes. */
export function germanHolidays(year, state) {
    const cacheKey = `${year}:${state ?? "-"}`;
    const cached = holidayCache.get(cacheKey);
    if (cached)
        return cached;
    const easter = easterSunday(year);
    const days = new Set([
        `${year}-01-01`, // Neujahr
        addDays(easter, -2), // Karfreitag
        addDays(easter, 1), // Ostermontag
        `${year}-05-01`, // Tag der Arbeit
        addDays(easter, 39), // Christi Himmelfahrt
        addDays(easter, 50), // Pfingstmontag
        `${year}-10-03`, // Tag der Deutschen Einheit
        `${year}-12-25`,
        `${year}-12-26`,
    ]);
    const corpusChristi = addDays(easter, 60);
    if (state && ["BW", "BY", "HE", "NW", "RP", "SL"].includes(state))
        days.add(corpusChristi);
    if (state && ["BW", "BY", "ST"].includes(state))
        days.add(`${year}-01-06`); // Heilige Drei Koenige
    if (state && ["BW", "BY", "NW", "RP", "SL"].includes(state))
        days.add(`${year}-11-01`); // Allerheiligen
    if (state && ["BB", "MV", "SN", "ST", "TH", "HH", "HB", "NI", "SH"].includes(state)) {
        days.add(`${year}-10-31`); // Reformationstag
    }
    if (state === "SL" || state === "BY")
        days.add(`${year}-08-15`); // Mariae Himmelfahrt (BY teilweise)
    if (state === "BE")
        days.add(`${year}-03-08`); // Internationaler Frauentag
    if (state === "SN") {
        // Buss- und Bettag: Mittwoch vor dem 23. November.
        let d = `${year}-11-22`;
        while (parseIsoDate(d).getUTCDay() !== 3)
            d = addDays(d, -1);
        days.add(d);
    }
    holidayCache.set(cacheKey, days);
    return days;
}
export function isBankHoliday(iso, state) {
    return germanHolidays(Number(iso.slice(0, 4)), state).has(iso);
}
/** Bankarbeitstag = kein Wochenende und kein gesetzlicher Feiertag. */
export function isBusinessDay(iso, state) {
    return !isWeekend(iso) && !isBankHoliday(iso, state);
}
/** Der Tag selbst, falls Werktag — sonst der naechstfolgende Werktag (§ 108 AO). */
export function nextBusinessDay(iso, state) {
    let cur = iso;
    for (let i = 0; i < 30; i++) {
        if (isBusinessDay(cur, state))
            return cur;
        cur = addDays(cur, 1);
    }
    return cur;
}
/** Der Tag selbst, falls Werktag — sonst der letzte vorangehende Werktag. */
export function previousBusinessDay(iso, state) {
    let cur = iso;
    for (let i = 0; i < 30; i++) {
        if (isBusinessDay(cur, state))
            return cur;
        cur = addDays(cur, -1);
    }
    return cur;
}
/**
 * Der n-letzte Bankarbeitstag eines Monats (n = 1 → letzter).
 * Gebraucht fuer Lohnzahlungen und den SV-Beitrag (drittletzter Bankarbeitstag).
 */
export function nthLastBusinessDayOfMonth(key, n, state) {
    let cur = endOfMonth(key);
    let found = 0;
    for (let i = 0; i < 45; i++) {
        if (isBusinessDay(cur, state)) {
            found++;
            if (found === n)
                return cur;
        }
        cur = addDays(cur, -1);
    }
    return cur;
}
/**
 * Setzt einen Zahltag innerhalb eines Monats, robust gegen kurze Monate:
 * Tag 31 im Februar wird zum 28./29., nicht zum 3. Maerz.
 */
export function dayInMonth(key, day) {
    const last = Number(endOfMonth(key).slice(8, 10));
    const clamped = Math.min(Math.max(day, 1), last);
    return `${key}-${String(clamped).padStart(2, "0")}`;
}
/** Heute als ISO-Datum in deutscher Zeitzone — nicht in der Zeitzone des Servers. */
export function todayInBerlin(now = new Date()) {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Berlin",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(now);
}
//# sourceMappingURL=dates.js.map